import {
  ArrowLeft,
  Balloon,
  CalendarDots,
  CaretDown,
  ChatCircleText,
  CheckSquare,
  ForkKnife,
  GearSix,
  Lock,
  LockOpen,
  House,
  Plus,
  SignOut,
  Star,
  UsersThree,
  WifiSlash,
} from '@phosphor-icons/react';
import {
  Avatar,
  Button,
  Heading,
  Icon,
  IconButton,
  SideNav,
  TabBar,
  buttonStyles,
  cn,
  useDaylight,
  type TabBarItem,
} from '@shh/ui';
import { useEffect, useState } from 'react';
import { useDeviceSeen } from '../lib/deviceSeen';
import { Button as AriaButton } from 'react-aria-components';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { endParentSession, extendParentSession } from '../data/api';
import { currentDaypart, memberById, pendingInstances, stateOf, tasksOf } from '../data/derive';
import { useFamily } from '../data/family';
import { setAgeProfile, setDeviceContext } from '../lib/display';
import { foodTabHref, rememberFoodTab } from '../lib/foodTab';
import { useFirstFoodTab } from '../components/recipes/FoodTabs';
import { useWakeLock } from '../lib/useWakeLock';
import { useFullscreen } from '../lib/fullscreen';
import { useEnsureRegion } from '../lib/region';
import { useCanFeedback } from '../pages/FeedbackPage';
import { AddSheet } from './AddSheet';
import { JobEditProvider } from '../components/JobEdit';
import { HeaderGlance, useScrollY } from './HeaderGlance';
import { LensSheet } from './LensSheet';
import { RestScreen, useRest } from './Rest';
import { useLens } from './lens';
import { setLens } from './view';
import { APP_NAME, PARENT_SESSION_MS, skyPlace, timeIn } from '@shh/shared';
import { appText } from '../i18n/app';

const TITLES: Record<string, string> = {
  '/': appText.nav.today,
  '/kalender': appText.nav.calendar,
  '/aufgaben': appText.nav.tasks,
  '/essen': appText.nav.food,
  '/sterne': appText.nav.stars,
  '/einstellungen': appText.titles.settings,
  '/feedback': appText.titles.feedback,
  '/freizeit': appText.nav.fun,
  '/freizeit/ideen': appText.titles.ideas,
  '/freizeit/gutscheine': appText.titles.vouchers,
};

/** Offline banner: the app keeps working, changes are queued (Firestore offline cache). */
function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/**
 * One shell for every person and every device:
 *  - the page title in the header always says where you are
 *  - the same five destinations, as a bottom bar on phones and a rail on tablets / the wall
 *  - detail pages (a person, settings) sit one level below "Heute" and show the way back
 *  - "＋" adds something new from anywhere; the person chip ("lens") says whose view it is
 */
/**
 * The sticky header's height as `--shh-header-h`, so rows inside a page (the day plan's pinned
 * all-day chips, 1.24) can stick right below it at any text size.
 */
function useHeaderHeight() {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!el) return;
    const set = () =>
      document.documentElement.style.setProperty('--shh-header-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return setEl;
}

/** The longest a wall stays in the cooking view without a touch. */
const COOKING_IDLE_MS = 3 * 60 * 60_000;

export function Shell() {
  const f = useFamily();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lens = useLens();
  const firstFoodTab = useFirstFoodTab();
  // remembered here, not in the tab page: the page loads lazily, a quick tap away lost it
  useEffect(() => rememberFoodTab(pathname), [pathname]);
  const headerRef = useHeaderHeight();
  useEnsureRegion();
  const online = useOnline();
  const canFeedback = useCanFeedback();
  const [lensOpen, setLensOpen] = useState(false);
  // the wall's gear while locked: PIN first, then straight into the settings (Mike, 05.10.)
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const place = f.household?.location;

  // Colours follow the sun on every device (family setting, "subtle" unless parents chose
  // otherwise). At night phones switch to the dark theme, the wall to its warm night theme.
  const daylight = f.household?.daylight ?? 'subtle';
  useDaylight({
    ...skyPlace(place, f.timeZone),
    strength: daylight,
    nightTheme: lens.isWall ? 'wall-night' : 'dark',
    enabled: daylight !== 'off',
  });
  // Wall behaviour: screen stays on, larger scale.
  useWakeLock(lens.isWall);
  useDeviceSeen(lens.isWall, f.today);
  // full screen when this device asked for it (Einstellungen → Anzeige)
  useFullscreen();
  useEffect(() => setDeviceContext(lens.isWall ? 'wall' : undefined), [lens.isWall]);
  // Age profile follows the lens: Ben gets big pictures – on the same pages.
  useEffect(() => {
    setAgeProfile(
      lens.member && lens.member.role !== 'parent' ? lens.member.ageProfile : undefined,
    );
  }, [lens.member]);
  // Header: see-through at the very top (the daylight glow shows through – an opaque bar broke
  // the colour, Mike 02.10.), solid once content scrolls under it. On "Heute" the time and the
  // weather dock into it when the big clock has scrolled away; elsewhere they are always there.
  const scrollY = useScrollY();
  const atTop = scrollY < 4;
  const glance = pathname !== '/' || scrollY > 140;
  // Cooking at the wall: floury hands touch nothing for minutes – neither the rest screen nor
  // the way back to "Heute" may end the cooking view (Mike, 17.10.: "springt der dann immer in
  // den Ruhemodus … dann ist anschließend der Kochmodus weg").
  const cooking = pathname.endsWith('/kochen');
  // Family Atmosphere: the wall rests after a few minutes without a touch (family setting).
  const rest = useRest(lens.isWall && !cooking);
  useKeepParentMode(lens.isWall && lens.canParent);
  // Kiosk rule: after 90 s without input the wall returns to the family view on "Heute" – while
  // cooking only after hours, so a forgotten recipe does not stay on the wall for good.
  useEffect(() => {
    if (!lens.isWall || (lens.id === 'all' && pathname === '/' && !lens.canParent)) return;
    // the parent mode keeps its 2 minutes from the last touch (Mike, 24.10.)
    const idle = cooking ? COOKING_IDLE_MS : lens.canParent ? PARENT_SESSION_MS : 90_000;
    let t = window.setTimeout(reset, idle);
    function reset() {
      setLens('all');
      void endParentSession().catch(() => undefined);
      navigate('/');
    }
    const bump = () => {
      window.clearTimeout(t);
      t = window.setTimeout(reset, idle);
    };
    const evs = ['pointerdown', 'keydown', 'scroll'] as const;
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      window.clearTimeout(t);
      evs.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [lens.isWall, lens.id, lens.canParent, pathname, navigate, cooking]);

  const daypart = currentDaypart(timeIn(f.timeZone));
  const openForLens = lens.member
    ? tasksOf(f, lens.member.id, daypart).filter((t) => stateOf(f, t.id) === 'open').length
    : 0;
  const waiting = pendingInstances(f).length + f.redemptions.length;

  const nav: TabBarItem[] = [
    { id: '/', label: appText.nav.today, icon: House, href: '/' },
    { id: '/kalender', label: appText.nav.calendar, icon: CalendarDots, href: '/kalender' },
    {
      id: '/aufgaben',
      label: appText.nav.tasks,
      icon: CheckSquare,
      href: '/aufgaben',
      badge: openForLens || undefined,
    },
    {
      id: '/sterne',
      label: appText.nav.stars,
      icon: Star,
      href: '/sterne',
      badge: lens.canParent && waiting ? waiting : undefined,
    },
    { id: '/freizeit', label: appText.nav.fun, icon: Balloon, href: '/freizeit' },
  ];
  // "Essen" sits between "Aufgaben" and "Sterne" (1.20)
  // "Essen" opens the tab used last, else the first one (Mike, 16.10.)
  nav.splice(3, 0, {
    id: '/essen',
    label: appText.nav.food,
    icon: ForkKnife,
    href: foodTabHref(firstFoodTab),
  });
  // Phones keep four fixed places, the rest is one pull away (Mike, 14.10.: variant B). Children
  // and teens get "Sterne" there instead of "Essen" – that is what they open most.
  const young = Boolean(lens.member && lens.member.role !== 'parent');
  const fixed = ['/', '/kalender', '/aufgaben', young ? '/sterne' : '/essen'];
  const more = {
    items: nav,
    links: [
      ...(lens.canParent
        ? [{ id: '/einstellungen', label: appText.settings, icon: GearSix, href: '/einstellungen' }]
        : []),
      ...(canFeedback
        ? [
            {
              id: '/feedback',
              label: appText.titles.feedback,
              icon: ChatCircleText,
              href: '/feedback',
            },
          ]
        : []),
    ],
  };
  const personId = pathname.startsWith('/person/') ? pathname.split('/')[2] : undefined;
  // Freizeit lists sit one level below the Freizeit overview (and lead back there)
  const inLeisure = pathname.startsWith('/freizeit/');
  // recipes and the cooking view (1.21) are part of "Essen" – that tab stays marked
  const inFood = pathname.startsWith('/essen/');
  const isDetail =
    Boolean(personId) || pathname === '/einstellungen' || pathname === '/feedback' || inLeisure;
  const title = personId
    ? (memberById(f, personId)?.name ?? appText.titles.person)
    : pathname.startsWith('/freizeit/wuensche/')
      ? appText.titles.wishes
      : (TITLES[pathname] ?? (inFood ? appText.nav.food : APP_NAME));
  // Detail pages belong to "Heute" (or Freizeit) – that tab stays marked, so you always know
  // where you are.
  const activeId = inLeisure ? '/freizeit' : inFood ? '/essen' : isDetail ? '/' : pathname;
  const back = inLeisure
    ? { to: '/freizeit', label: appText.nav.fun }
    : { to: '/', label: appText.nav.today };

  return (
    <JobEditProvider>
      {/* while resting the dashboard stays mounted (live data) but cannot be reached */}
      <div className="relative isolate flex min-h-dvh" inert={rest.mode !== 'awake'}>
        {lens.isWall && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-80 bg-linear-to-b from-ambient-glow to-transparent"
          />
        )}

        <aside className="sticky top-0 hidden h-dvh w-28 shrink-0 flex-col gap-gap-lg border-r border-border-subtle px-inset-xs pt-safe lg:flex">
          {/* The brand, not a menu item: the colourful logo looked nothing like the green "Heute"
              house it replaced, which people kept tapping by mistake (Mike, 07.10.). */}
          <Link
            to="/"
            aria-label={appText.toToday(APP_NAME)}
            className="flex flex-col items-center gap-gap-xs rounded-card pt-gap-xs outline-none focus-visible:outline-3 focus-visible:outline-border-focus"
          >
            <img src="/icon.svg" alt="" className="size-avatar-md rounded-control" />
            <span className="text-center text-xs font-bold leading-tight text-text-secondary">
              {APP_NAME}
            </span>
          </Link>
          <SideNav items={nav} activeId={activeId} />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {lens.isWall && lens.canParent && <ParentSessionBar until={f.parentUntil} />}
          {!online && (
            <div
              role="status"
              className="flex items-center gap-gap-sm bg-feedback-warning-surface px-safe py-inset-xs text-sm font-semibold text-feedback-warning"
            >
              <Icon icon={WifiSlash} size="sm" /> {appText.offline}
            </div>
          )}

          <header
            ref={headerRef}
            className={cn(
              'sticky top-0 z-20 flex items-center gap-gap-md border-b px-safe py-inset-sm transition-colors duration-(--shh-motion-duration-base)',
              atTop
                ? 'border-transparent bg-transparent'
                : 'border-border-subtle bg-surface-canvas',
            )}
          >
            {isDetail && (
              <Link to={back.to} className={cn(buttonStyles({ variant: 'quiet' }), 'px-inset-xs')}>
                <Icon icon={ArrowLeft} size="md" weight="bold" />
                <span className="max-sm:sr-only">{back.label}</span>
              </Link>
            )}
            <Heading level={1} size="lg" className="min-w-0 shrink truncate">
              {title}
            </Heading>
            {glance && <HeaderGlance />}
            <div className="ml-auto flex items-center gap-gap-sm">
              <IconButton
                icon={Plus}
                iconWeight="bold"
                variant="primary"
                aria-label={appText.add}
                onPress={() => setAddOpen(true)}
              />
              {/* the wall shows a lock while locked; the PIN opens the parent mode and stays on
                  this page (Mike, 24.10.: "Das Zahnrad braucht es dann nicht mehr"; 25.10.: "das
                  Schloss an der Stelle ohne Text … nur Hover-Text") */}
              {!lens.canParent && lens.isWall && (
                <span title={appText.parentModeOpen} className="contents">
                  <IconButton
                    variant="secondary"
                    icon={Lock}
                    aria-label={appText.parentModeOpen}
                    onPress={() => setUnlockOpen(true)}
                  />
                </span>
              )}
              {lens.canParent && !lens.isWall && (
                <Link
                  to="/einstellungen"
                  aria-label={appText.settings}
                  aria-current={pathname === '/einstellungen' ? 'page' : undefined}
                  // On phones the gear moves into the person menu to leave room for the title.
                  className={cn(
                    buttonStyles({ variant: 'quiet' }),
                    'aspect-square px-0 max-sm:hidden',
                  )}
                >
                  <Icon icon={GearSix} size="md" />
                </Link>
              )}
              <AriaButton
                onPress={() => setLensOpen(true)}
                aria-label={appText.viewOf(lens.member ? lens.member.name : appText.everyone)}
                className={cn(
                  'flex min-h-touch-min cursor-pointer items-center gap-gap-sm rounded-chip border border-border-default bg-surface-raised py-1 pr-inset-sm pl-1',
                  'outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover data-pressed:scale-97',
                )}
              >
                {lens.member ? (
                  <Avatar
                    name={lens.member.name}
                    color={lens.member.color}
                    emoji={lens.member.emoji}
                    size="sm"
                    decorative
                  />
                ) : (
                  <span className="grid size-avatar-sm place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
                    <Icon icon={UsersThree} size="sm" weight="fill" />
                  </span>
                )}
                <span className="font-bold max-sm:sr-only">
                  {lens.member ? lens.member.name : appText.everyone}
                </span>
                <Icon icon={CaretDown} size="sm" weight="bold" />
              </AriaButton>
            </div>
          </header>

          <main className="flex w-full flex-1 flex-col gap-section px-safe py-section">
            <Outlet />
          </main>

          <TabBar
            items={nav.filter((i) => fixed.includes(i.id))}
            more={more}
            activeId={activeId}
            className="sticky bottom-0 z-20 lg:hidden"
          />
        </div>

        <LensSheet isOpen={lensOpen} onOpenChange={setLensOpen} />
        <LensSheet isOpen={unlockOpen} onOpenChange={setUnlockOpen} forSettings />
        <AddSheet
          isOpen={addOpen}
          onOpenChange={setAddOpen}
          onUnlock={() => {
            setAddOpen(false);
            setUnlockOpen(true);
          }}
        />
      </div>
      {rest.mode !== 'awake' && <RestScreen mode={rest.mode} onWake={rest.wake} />}
    </JobEditProvider>
  );
}

function ParentSessionBar({ until }: { until: number }) {
  const [left, setLeft] = useState(() => Math.max(0, Math.round((until - Date.now()) / 1000)));
  useEffect(() => {
    const t = window.setInterval(
      () => setLeft(Math.max(0, Math.round((until - Date.now()) / 1000))),
      1000,
    );
    return () => window.clearInterval(t);
  }, [until]);
  if (left === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-gap-sm bg-surface-inverse px-safe py-inset-xs text-text-inverse">
      <span className="flex items-center gap-gap-xs font-semibold tabular-nums">
        <Icon icon={LockOpen} size="sm" weight="bold" />
        {appText.parentModeLeft(`${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`)}
      </span>
      <span className="flex items-center gap-gap-sm">
        <Link to="/einstellungen" className={buttonStyles({ variant: 'secondary' })}>
          <Icon icon={GearSix} size="md" /> {appText.settings}
        </Link>
        <Button
          variant="secondary"
          icon={SignOut}
          onPress={() => {
            setLens('all');
            void endParentSession();
          }}
        >
          {appText.end}
        </Button>
      </span>
    </div>
  );
}

/**
 * Parent mode on the wall ends 2 minutes after the last touch (Mike, 24.10.): touches extend the
 * session on the server – at most every 20 s, so it ends between 1:40 and 2:00 after the last one.
 */
function useKeepParentMode(active: boolean) {
  useEffect(() => {
    if (!active) return;
    let last = Date.now();
    const touch = () => {
      if (Date.now() - last < 20_000) return;
      last = Date.now();
      void extendParentSession().catch(() => undefined);
    };
    const evs = ['pointerdown', 'keydown'] as const;
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, touch));
  }, [active]);
}
